import * as React from 'react';
import styled from 'styled-components';

import { Loader } from '../../atoms';
import { getActivityFees } from '../../api/activities';
import { formatDateTime } from '../../utils/formatDate';
import { DEFAULT_DATE_FORMAT } from '../../constants';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const toMoney = (value: unknown): string => {
  const n = Number(value);
  return Number.isFinite(n) ? currencyFormatter.format(n) : '—';
};

const getInvoiceNumber = (invoiceNumber?: string): string => {
  if (!invoiceNumber) {
    return '—';
  }
  if (invoiceNumber.indexOf('----') > -1) {
    return invoiceNumber.split('----')[0];
  }
  return invoiceNumber;
};

const fmtDate = (date?: string | null): string => {
  if (!date) return '—';
  return formatDateTime(date, DEFAULT_DATE_FORMAT) || '—';
};

type StatusTone = 'green' | 'blue' | 'amber' | 'red' | 'neutral';

const statusTone = (status?: string): StatusTone => {
  const s = (status || '').toLowerCase();
  if (/paid|approved|issued|complete/.test(s)) return 'green';
  if (/sent|invoiced|progress|processing/.test(s)) return 'blue';
  if (/pending|waiting|hold|partial|unpaid/.test(s)) return 'amber';
  if (/void|denied|rejected|overdue|cancel/.test(s)) return 'red';
  return 'neutral';
};

interface ActivityFee {
  id: number;
  name?: string;
  amount?: number | string;
  quantity?: number | string;
  total?: number | string;
  status?: string;
  invoiceNumber?: string;
  dateInvoiced?: string | null;
  datePaid?: string | null;
}

const ViewActivityFees = ({ activityId }: { activityId?: number }) => {
  const [loading, setLoading] = React.useState(true);
  const [fees, setFees] = React.useState<ActivityFee[]>([]);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    if (!activityId) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError(false);
    getActivityFees({}, activityId)
      .then(response => {
        if (!active) return;
        const data = Array.isArray(response?.data) ? response.data : [];
        setFees(data);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [activityId]);

  const grandTotal = React.useMemo(
    () =>
      fees.reduce((sum, fee) => {
        const n = Number(fee.total);
        return Number.isFinite(n) ? sum + n : sum;
      }, 0),
    [fees],
  );

  if (loading) {
    return (
      <Centered>
        <Loader />
      </Centered>
    );
  }

  if (error) {
    return <EmptyState>Unable to load fees. Please try again.</EmptyState>;
  }

  if (!fees.length) {
    return <EmptyState>No fees found for this activity.</EmptyState>;
  }

  return (
    <Wrapper>
      <SummaryBar>
        <SummaryItem>
          <SummaryLabel>Fees</SummaryLabel>
          <SummaryValue>{fees.length}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Grand Total</SummaryLabel>
          <SummaryValue>{toMoney(grandTotal)}</SummaryValue>
        </SummaryItem>
      </SummaryBar>

      <List>
        {fees.map(fee => (
          <FeeCard key={fee.id}>
            <FeeHeader>
              <FeeName>{fee.name || 'Fee'}</FeeName>
              <Chip tone={statusTone(fee.status)}>{fee.status || 'N/A'}</Chip>
            </FeeHeader>
            <FieldGrid>
              <Field>
                <FieldLabel>Invoice #</FieldLabel>
                <FieldValue>{getInvoiceNumber(fee.invoiceNumber)}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Amount</FieldLabel>
                <FieldValue>{toMoney(fee.amount)}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Quantity</FieldLabel>
                <FieldValue>{fee.quantity ?? '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Total</FieldLabel>
                <FieldValue $strong>{toMoney(fee.total)}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Date Invoiced</FieldLabel>
                <FieldValue>{fmtDate(fee.dateInvoiced)}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Date Paid</FieldLabel>
                <FieldValue>{fmtDate(fee.datePaid)}</FieldValue>
              </Field>
            </FieldGrid>
          </FeeCard>
        ))}
      </List>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue',
    Arial, Roboto, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji',
    'Segoe UI Symbol';
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 160px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const SummaryBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
`;

const SummaryItem = styled.div`
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  box-sizing: border-box;
`;

const SummaryLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 55vh;
  overflow-y: auto;
`;

const FeeCard = styled.div`
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-0);
  box-shadow: var(--shadow-xs);
  padding: 16px 18px;
  box-sizing: border-box;
`;

const FeeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--border);
`;

const FeeName = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
  word-break: break-word;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px 24px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const FieldLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const FieldValue = styled.span<{ $strong?: boolean }>`
  font-size: 0.875rem;
  font-weight: ${({ $strong }) => ($strong ? 700 : 500)};
  color: var(--mainText);
  word-break: break-word;
`;

const toneStyles: Record<StatusTone, { bg: string; fg: string; border: string }> = {
  green: { bg: 'var(--success-50)', fg: 'var(--success-700)', border: 'var(--success-100)' },
  blue: { bg: 'var(--primary-50)', fg: 'var(--primary-700)', border: 'var(--primary-100)' },
  amber: { bg: 'var(--warning-50)', fg: 'var(--warning-700)', border: 'var(--warning-100)' },
  red: { bg: 'var(--danger-50)', fg: 'var(--danger-700)', border: 'var(--danger-100)' },
  neutral: { bg: 'var(--neutral-100)', fg: 'var(--neutral-600)', border: 'var(--border)' },
};

const Chip = styled.span<{ tone: StatusTone }>`
  flex-shrink: 0;
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ tone }) => toneStyles[tone].bg};
  color: ${({ tone }) => toneStyles[tone].fg};
  border: 1px solid ${({ tone }) => toneStyles[tone].border};
`;

export default ViewActivityFees;
